import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { DateHelper } from '@helpers/date.helper';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'organization-services-payment-preference-card',
  templateUrl: './organization-services-payment-preference-card.component.html',
  styleUrls: ['./organization-services-payment-preference-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationServicesPaymentPreferenceCardComponent implements OnInit, OnDestroy {
  public isTablet$: Observable<boolean>;
  public formGroup: UntypedFormGroup;
  public enumCompanyPaymentType: typeof enumCompanyPaymentType = enumCompanyPaymentType;
  public isPaymentPreferenceEdit: boolean = false;
  public isOpenChangeToMonthlyDisclaimerModal: boolean;
  public monthlyNextPaymentDate: string;
  public changeToMonthlyDisclaimerInsructions: string[];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  @Input() public organization: OrganizationModel;
  @Output() public updatePaymentType: EventEmitter<enumCompanyPaymentType> = new EventEmitter<enumCompanyPaymentType>();

  constructor(
    private translateService: TranslateService,
    private genericService: GenericService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const today = new Date().toISOString();
    this.monthlyNextPaymentDate = DateHelper.startOfTheNextMonth(today, 'DD.MM.YY');

    this.isTablet$ = this.genericService.isTablet$;
    this.formGroup = this.fb.group({
      paymentTypePreference: new UntypedFormControl(!!this.organization?.customsPaymentType),
    });

    this.formGroup
      .get('paymentTypePreference')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((paymentTypePreference) => {
        const currentOrganizationFormPaymentType = paymentTypePreference ? enumCompanyPaymentType.MONTHLY : enumCompanyPaymentType.INSTANT;
        if (
          paymentTypePreference &&
          currentOrganizationFormPaymentType !== this.organization?.customsPaymentType &&
          !this.organization?.defaultPaymentMethod?.id
        ) {
          this.setIsOpenChangeToMonthlyDisclaimerModal(true);
        }
      });

    this.getChangeToMonthlyDisclaimerInsructions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((disclaimerInstructions: string[]) => {
        this.changeToMonthlyDisclaimerInsructions = disclaimerInstructions;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public openPaymentPreferenceEdit() {
    this.isPaymentPreferenceEdit = true;
  }

  public closePaymentPreferenceEdit() {
    this.isPaymentPreferenceEdit = false;
  }

  public onSave() {
    const paymentTypePreferenceValue = this.formGroup.get('paymentTypePreference').value;
    const customsPaymentType = paymentTypePreferenceValue ? enumCompanyPaymentType.MONTHLY : enumCompanyPaymentType.INSTANT;
    this.updatePaymentType.emit(customsPaymentType);
    this.closePaymentPreferenceEdit();
  }

  public setIsOpenChangeToMonthlyDisclaimerModal(state: boolean) {
    this.isOpenChangeToMonthlyDisclaimerModal = state;
  }

  public get organizationPaymentTypeButtonText(): string {
    return this.organization?.customsPaymentType ? 'LABELS.CHANGE_TO_INSTANT_PAYMENT' : 'LABELS.CHANGE_TO_MONTHLY_PAYMENT';
  }

  public get organizationPaymentTypeText(): string {
    return `LABELS.${enumCompanyPaymentType[this.organization?.customsPaymentType]}_PAYMENT`;
  }

  public get organizationFormPaymentType(): enumCompanyPaymentType {
    return this.formGroup?.get('paymentTypePreference').value ? enumCompanyPaymentType.MONTHLY : enumCompanyPaymentType.INSTANT;
  }

  public get isMonthly(): boolean {
    return this.organization?.customsPaymentType === enumCompanyPaymentType.MONTHLY;
  }

  public get isSaveChangesDisabled(): boolean {
    return (
      this.organizationFormPaymentType === this.organization?.customsPaymentType ||
      (this.organizationFormPaymentType === enumCompanyPaymentType.MONTHLY && !this.organization?.defaultPaymentMethod?.id)
    );
  }

  private getChangeToMonthlyDisclaimerInsructions(): Observable<any> {
    return this.translateService.get('ORGANIZATION.SERVICE_PAYMENT_PREFERENCE_DISCLAIMER.INSTRUCTIONS');
  }
}
